import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { RoleGuard } from "@/components/RoleGuard";
import { Button } from "@/components/ui/button";
import { useNFDetalhe } from "@/context/useAtualizacaoNF";
import { CancelarNFModal } from "@/components/CancelarNFModal";
import { ReprocessarNotaButton, PODE_REPROCESSAR } from "@/components/ReprocessarNotaButton";
import {
  ArquivoNF,
  CabecalhoNF,
  CamposNF,
  EstadoDetalheNF,
  HistoricoNF,
  LayoutDetalheNF,
  MotivoRecusaNF,
} from "@/components/nf/DetalheNFPartes";
import { Ban, FileUp } from "lucide-react";

const VOLTAR = { to: "/colaborador/notas", label: "Minhas NFs" };

const DetalheNFColab = () => {
  const { id } = useParams();
  const { nf, carregando, atualizar } = useNFDetalhe(id);
  const [modalAberto, setModalAberto] = useState(false);

  return (
    <RoleGuard role={["colaborador", "rh", "financeiro"]}>
      <AppLayout title="Detalhe da NF">
        <div className="mx-auto flex max-w-[1120px] flex-col gap-4">
          {carregando || !nf ? (
            <EstadoDetalheNF carregando={carregando}>
              NF não encontrada.{" "}
              <Link to="/colaborador/notas" className="text-primary-text underline">
                Ver minhas NFs
              </Link>
            </EstadoDetalheNF>
          ) : (
            <>
              <CabecalhoNF nf={nf} voltar={VOLTAR} />

              <LayoutDetalheNF
                principal={
                  <>
                    <CamposNF nf={nf} />
                    <ArquivoNF nf={nf} />
                  </>
                }
                lateral={
                  <>
                    {nf.status === "Recusada" && (
                      <>
                        {nf.motivoRecusa && <MotivoRecusaNF nf={nf} />}
                        {nf.duplicada ? (
                          // Cópia de uma NF já enviada: cancelar o documento cancelaria a nota válida.
                          <section className="flex flex-col gap-3 rounded-xl border border-amber-500/40 bg-amber-500/5 p-[18px]">
                            <div className="flex flex-col gap-1">
                              <span className="text-[15px] font-semibold text-amber-800 dark:text-amber-400">Ação necessária</span>
                              <p className="text-[13px] leading-snug text-muted-foreground">
                                Esta nota já tinha sido enviada antes. <strong>Não cancele</strong> — ela é a sua nota
                                válida. Envie a nota correta desta competência: ela substitui esta.
                              </p>
                            </div>
                            <Button
                              asChild
                              variant="outline"
                              className="w-full border-amber-500/40 text-amber-700 hover:bg-amber-500/10 hover:text-amber-800 dark:text-amber-400"
                            >
                              <Link to={`/colaborador/nova?substituir=${nf.id}`}>
                                <FileUp /> Enviar nota correta
                              </Link>
                            </Button>
                          </section>
                        ) : (
                        <>
                        <section className="flex flex-col gap-3 rounded-xl border border-amber-500/40 bg-amber-500/5 p-[18px]">
                          <div className="flex flex-col gap-1">
                            <span className="text-[15px] font-semibold text-amber-800 dark:text-amber-400">Ação necessária</span>
                            <p className="text-[13px] leading-snug text-muted-foreground">
                              Envie a nota fiscal com carimbo de <strong>CANCELADA</strong> para liberar o envio de uma nova nota.
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            className="w-full border-amber-500/40 text-amber-700 hover:bg-amber-500/10 hover:text-amber-800 dark:text-amber-400"
                            onClick={() => setModalAberto(true)}
                          >
                            <Ban /> Enviar nota cancelada
                          </Button>
                        </section>
                        <CancelarNFModal
                          nf={nf}
                          open={modalAberto}
                          onClose={() => setModalAberto(false)}
                          onSuccess={(nfAtualizada) => {
                            atualizar(nfAtualizada);
                            setModalAberto(false);
                          }}
                        />
                        </>
                        )}
                      </>
                    )}

                    {PODE_REPROCESSAR.includes(nf.status) && (
                      // O botão some sozinho quando o usuário não pode reprocessar (ex.: aprovada fora do RH).
                      <div className="flex flex-col empty:hidden [&>button]:w-full">
                        <ReprocessarNotaButton nf={nf} />
                      </div>
                    )}

                    <HistoricoNF nf={nf} />
                  </>
                }
              />
            </>
          )}
        </div>
      </AppLayout>
    </RoleGuard>
  );
};

export default DetalheNFColab;
